import type { ReactNode } from 'react';
import { Activity } from 'lucide-react';
import { CERTIFICATE_WARNING_MS } from '@cp/shared';
import { Card, Fact, Facts, HttpStatus } from '@/components/ui';
import { formatDateTime, formatRelative } from '@/utils/format';
import type { SiteDetail } from '@/types/api';

/**
 * What the availability checks last saw (P6): the answer a visitor gets, how
 * long it took, the redirects on the way, the certificate — and the same page
 * asked by the node itself, which tells "the site is down" from "the way to
 * it is".
 */
export function AvailabilityCard({ site }: { readonly site: SiteDetail }): ReactNode {
  const m = site.monitoring;
  if (!m.checkedAt && !m.originCheckedAt) {
    return (
      <Card title="Доступность" icon={Activity}>
        <p className="field-hint">Проверок ещё не было.</p>
      </Card>
    );
  }

  const tlsExpiresSoon = m.tlsExpiresAt !== null && new Date(m.tlsExpiresAt).getTime() - Date.now() < CERTIFICATE_WARNING_MS;

  return (
    <Card title="Доступность" icon={Activity}>
      <Facts>
        <Fact label="Ответ снаружи">
          {m.httpStatus ? <HttpStatus code={m.httpStatus} /> : <span className="tone-down">{m.checkError ?? 'нет ответа'}</span>}
          {m.httpStatus && m.checkError ? <span className="tone-down"> · {m.checkError}</span> : null}
          {m.responseMs !== null ? <span className="muted"> · {m.responseMs} мс</span> : null}
          {m.consecutiveFailures > 0 ? <span className="tone-down"> · неудач подряд: {m.consecutiveFailures}</span> : null}
        </Fact>
        <Fact label="Проверено">{m.checkedAt ? formatRelative(m.checkedAt) : 'ещё нет'}</Fact>
        <Fact label="Последний успех">{m.lastOkAt ? formatRelative(m.lastOkAt) : 'ни разу'}</Fact>
        {m.redirects.length > 0 ? (
          <Fact label="Перенаправления">
            <span className="text-sm row-tight row-wrap">
              {m.redirects.map((hop) => (
                <span key={`${hop.status}-${hop.url}`} className="row-tight">
                  <HttpStatus code={hop.status} />
                  <span className="mono">{hop.url}</span>
                  <span className="muted">→</span>
                </span>
              ))}
              <span className="mono">{m.finalUrl}</span>
            </span>
          </Fact>
        ) : null}
        <Fact label="Сертификат">
          {m.tlsExpiresAt ? (
            <span className={tlsExpiresSoon ? 'tone-degraded' : ''}>
              до {formatDateTime(m.tlsExpiresAt)}
              {m.tlsIssuer ? <span className="muted"> · {m.tlsIssuer}</span> : null}
            </span>
          ) : (
            <span className="faint">не прочитан</span>
          )}
        </Fact>
        {site.originCertificate ? (
          <Fact label="Сертификат сервера">
            {site.originCertificate.authenticated ? (
              <span>проверяемый (Full strict)</span>
            ) : (
              <span className="tone-degraded">самоподписанный (режим Full)</span>
            )}
            {site.originCertificate.expiresAt ? <span className="muted"> · до {formatDateTime(site.originCertificate.expiresAt)}</span> : null}
          </Fact>
        ) : null}
        <Fact label="С сервера сайта">
          {m.originCheckedAt ? (
            <>
              <HttpStatus code={m.originStatus} />
              {m.originMs !== null ? <span className="muted"> · {m.originMs} мс</span> : null}
              <span className="muted"> · {formatRelative(m.originCheckedAt)}</span>
            </>
          ) : (
            <span className="faint">нет данных</span>
          )}
        </Fact>
      </Facts>
      {m.checkError && m.originStatus && m.originStatus < 400 ? (
        <p className="field-hint">
          Сервер отвечает, но сайт недоступен снаружи. Проверьте DNS, Cloudflare и сеть.
        </p>
      ) : null}
    </Card>
  );
}
